import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarPlus, ChevronDown, X, AlertTriangle, Timer } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { QuoteRow } from '../types';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';
import { parseDateOnlyToLocalDate } from '../utils/dateOnly';
import { getBrowserTimeZone } from '../utils/timezoneUtils';
import toast from 'react-hot-toast';

const formatRequestNumber = (id: string) => (id ? `Q-${id.slice(0, 8)}` : '—');

function ServiceIconBadge({ icon }: { icon?: string | null }) {
  const raw = typeof icon === 'string' ? icon.trim() : '';
  const value = raw || '📦';
  const isImageSrc = /^https?:\/\//i.test(value) || value.startsWith('data:');
  return (
    <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md bg-gray-100 border border-gray-200">
      {isImageSrc ? (
        <img src={value} alt="" className="w-4 h-4 object-contain" />
      ) : (
        <span className="text-sm leading-none">{value}</span>
      )}
    </span>
  );
}

const CreateAppointmentFromQuotePage: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quote, setQuote] = useState<QuoteRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [assignmentByServiceId, setAssignmentByServiceId] = useState<Record<string, string>>({});
  const [openAssignDropdownServiceId, setOpenAssignDropdownServiceId] = useState<string | null>(null);
  const [showPro, setShowPro] = useState(true); // kept so any remaining Show Pro checkbox refs compile; checkbox UI removed
  const [date, setDate] = useState('');
  const [timeType, setTimeType] = useState<'exact' | 'window'>('window');
  const [exactTime, setExactTime] = useState('09:00');
  const [earliestTime, setEarliestTime] = useState('09:00');
  const [latestTime, setLatestTime] = useState('12:00');
  const [locationAddress, setLocationAddress] = useState('');
  const [messageToCustomer, setMessageToCustomer] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [depositRequired, setDepositRequired] = useState(true);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDeadlineValue, setDepositDeadlineValue] = useState(24);
  const [depositDeadlineUnit, setDepositDeadlineUnit] = useState<'hours' | 'days'>('hours');
  const [internalNotes, setInternalNotes] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringIntervalWeeks, setRecurringIntervalWeeks] = useState(1);
  const [recurringOccurrenceCount, setRecurringOccurrenceCount] = useState(2);

  const [professionals, setProfessionals] = useState<Array<{ id: string; name: string }>>([]);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);
  const [businessServices, setBusinessServices] = useState<
    Array<{ id: string; name?: string; icon?: string | null; category?: { icon?: string }; serviceLocation?: string | null }>
  >([]);
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(false);

  const loadQuote = useCallback(async () => {
    if (!quoteId) return;
    try {
      setLoading(true);
      const data = await apiService.getQuote(quoteId);
      setQuote(data);
      const serviceNames = (data.services || [])
        .map((s: { name?: string | null }) => s?.name?.trim())
        .filter(Boolean) as string[];
      const defaultTitle =
        serviceNames.length === 0
          ? 'Appointment'
          : serviceNames.length === 1
            ? serviceNames[0]
            : serviceNames.length === 2
              ? `${serviceNames[0]} & ${serviceNames[1]}`
              : `${serviceNames.slice(0, -1).join(', ')} & ${serviceNames[serviceNames.length - 1]}`;
      setTitle(defaultTitle);
      const total = data.businessOfferAmount ?? data.finalAgreedAmount;
      setTotalAmount(total != null ? Number(total).toFixed(2) : '');
      const dep = data.requiredDepositAmount;
      setDepositAmount(dep != null ? Number(dep).toFixed(2) : '');
      setDepositRequired(dep != null && Number(dep) > 0);
    } catch (err) {
      console.error('Error loading quote:', err);
      toast.error('Failed to load quote');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!quote?.businessId) return;
    setProfessionalsLoading(true);
    apiService
      .getBusinessProfessionalsDetailed(quote.businessId)
      .then((list: any[]) => {
        setProfessionals(
          (list || []).map((p: any) => ({
            id: p.id,
            name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || p.id
          }))
        );
      })
      .catch(() => setProfessionals([]))
      .finally(() => setProfessionalsLoading(false));
  }, [quote?.businessId]);

  // Single-personnel businesses: auto-assign every quote service to that pro and lock the dropdown.
  useEffect(() => {
    if (professionals.length !== 1 || !quote?.services?.length) return;
    const soleProId = professionals[0].id;
    if (!soleProId) return;
    setAssignmentByServiceId((prev) => {
      const next: Record<string, string> = { ...prev };
      let changed = false;
      for (const svc of quote.services) {
        const sid = typeof svc?.id === 'string' ? svc.id : String(svc?.id ?? '');
        if (!sid) continue;
        if (next[sid] !== soleProId) {
          next[sid] = soleProId;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setOpenAssignDropdownServiceId(null);
  }, [professionals, quote?.services]);

  useEffect(() => {
    if (!quote?.businessId) return;
    apiService
      .getBusinessServices(quote.businessId)
      .then((list: any[]) => setBusinessServices(list || []))
      .catch(() => setBusinessServices([]));
  }, [quote?.businessId]);

  useEffect(() => {
    if (!quote?.businessId) {
      setOnlinePaymentsEnabled(false);
      return;
    }
    let cancelled = false;
    apiService
      .getBusinessStripeAccount(quote.businessId)
      .then((data) => {
        if (!cancelled) setOnlinePaymentsEnabled(!!data?.chargesEnabled);
      })
      .catch(() => {
        if (!cancelled) setOnlinePaymentsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quote?.businessId]);

  const getServiceIcon = (serviceId: string): string | null => {
    const svc = businessServices.find((s) => String(s.id) === String(serviceId));
    if (!svc) return null;
    const icon = svc.icon ?? (svc as any).icon;
    if (typeof icon === 'string' && icon.trim() !== '') return icon;
    const catIcon = svc.category?.icon ?? (svc as any).categoryIcon;
    if (typeof catIcon === 'string' && catIcon.trim() !== '') return catIcon;
    return null;
  };

  const isOnSiteServiceLocation = (raw: unknown): boolean => {
    const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
    return loc === 'ON_SITE' || loc === 'ONSITE';
  };

  const quoteHasOnSiteService = (quote?.services ?? []).some((quoteSvc) => {
    const sid = typeof quoteSvc?.id === 'string' ? quoteSvc.id : String(quoteSvc?.id ?? '');
    if (!sid) return false;
    const businessSvc = businessServices.find((s) => String(s.id) === sid);
    const rawLoc =
      (businessSvc as any)?.serviceLocation ??
      (businessSvc as any)?.service_location ??
      (quoteSvc as any)?.serviceLocation ??
      (quoteSvc as any)?.service_location;
    return isOnSiteServiceLocation(rawLoc);
  });

  const goBack = () => navigate(`/dashboard/business/quote-requests/${quoteId}`);

  useEffect(() => {
    if (openAssignDropdownServiceId == null) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const containers = document.querySelectorAll('[data-assign-dropdown]');
      const insideAny = Array.from(containers).some((el) => el.contains(target));
      if (!insideAny) setOpenAssignDropdownServiceId(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openAssignDropdownServiceId]);

  const timeToLocalISO = (dateStr: string, timeStr: string): string => {
    if (!dateStr || !timeStr) return '';
    const parts = timeStr.split(':').map((p) => Number(p));
    const h = parts[0];
    const m = Number.isFinite(parts[1]) ? parts[1] : 0;
    if (!Number.isFinite(h)) return '';
    const d = parseDateOnlyToLocalDate(dateStr);
    if (!d) return '';
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quoteId || !quote || submitting) return;

    const dateVal = date.trim();
    if (!dateVal) {
      toast.error('Please select a date');
      return;
    }

    const assignments = (quote.services || []).map((s) => {
      const sid = typeof s?.id === 'string' ? s.id : String(s?.id ?? '');
      const proId = assignmentByServiceId[sid];
      return {
        serviceId: sid,
        professionalId: proId && proId.trim() ? proId.trim() : null
      };
    });

    const missingProfessional = assignments.some((a) => !a.professionalId);
    if (missingProfessional) {
      toast.error('Please assign a professional for each service');
      return;
    }

    let payload: {
      scheduledAt?: string;
      flexScheduledAt?: { earliest: string; latest: string };
      assignments: typeof assignments;
      clientTimeZone?: string;
      recurring?: { intervalWeeks: number; occurrenceCount: number };
      depositDeadline?: { value: number; unit: 'hours' | 'days' };
      appointmentTitle?: string;
      locationAddress?: string;
      messageToCustomer?: string;
    } = { assignments };

    if (title.trim()) {
      payload.appointmentTitle = title.trim();
    }
    if (quoteHasOnSiteService && locationAddress.trim()) {
      payload.locationAddress = locationAddress.trim();
    }
    if (messageToCustomer.trim()) {
      payload.messageToCustomer = messageToCustomer.trim();
    }

    if (depositRequired && depositNum != null && depositNum > 0) {
      const deadlineValue = Number.isFinite(depositDeadlineValue) ? Math.max(1, Math.round(depositDeadlineValue)) : 24;
      payload.depositDeadline = { value: deadlineValue, unit: depositDeadlineUnit };
    }

    if (recurringEnabled) {
      payload.clientTimeZone = getBrowserTimeZone();
      payload.recurring = {
        intervalWeeks: recurringIntervalWeeks,
        occurrenceCount: recurringOccurrenceCount,
      };
      if (timeType === 'exact') {
        const scheduledAt = timeToLocalISO(dateVal, exactTime);
        if (!scheduledAt) {
          toast.error('Please set the appointment time');
          return;
        }
        payload.scheduledAt = scheduledAt;
      } else {
        const earliest = timeToLocalISO(dateVal, earliestTime);
        const latest = timeToLocalISO(dateVal, latestTime);
        if (!earliest || !latest) {
          toast.error('Please set earliest and latest times');
          return;
        }
        if (new Date(latest).getTime() <= new Date(earliest).getTime()) {
          toast.error('Latest time must be after earliest time');
          return;
        }
        payload.flexScheduledAt = { earliest, latest };
      }
    } else if (timeType === 'exact') {
      const scheduledAt = timeToLocalISO(dateVal, exactTime);
      if (!scheduledAt) {
        toast.error('Please set the appointment time');
        return;
      }
      payload.scheduledAt = scheduledAt;
    } else {
      const earliest = timeToLocalISO(dateVal, earliestTime);
      const latest = timeToLocalISO(dateVal, latestTime);
      if (!earliest || !latest) {
        toast.error('Please set earliest and latest times');
        return;
      }
      if (new Date(latest).getTime() <= new Date(earliest).getTime()) {
        toast.error('Latest time must be after earliest time');
        return;
      }
      payload.flexScheduledAt = { earliest, latest };
    }

    setSubmitting(true);
    try {
      const data = await apiService.createAppointmentFromQuote(quoteId, payload);
      const n = data.appointmentIds?.length;
      toast.success(
        n != null && n > 1 ? `Recurring series created (${n} appointments)` : 'Appointment created'
      );
      navigate(`/appointments/${data.appointmentId}?from=quote-requests`);
    } catch (err: any) {
      console.error('Error creating appointment:', err);
      const message = err?.response?.data?.message || 'Failed to create appointment';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const totalNum = totalAmount.trim() === '' ? null : parseFloat(totalAmount);
  const depositNum = depositRequired && depositAmount.trim() !== '' ? parseFloat(depositAmount) : null;
  const remaining = totalNum != null && depositNum != null ? totalNum - depositNum : null;
  const quoteServices = quote?.services ?? [];
  const soleProfessional = professionals.length === 1 ? professionals[0] : null;
  const isSoleProfessionalLocked = Boolean(soleProfessional?.id);
  const showDepositPaymentInstructions =
    !onlinePaymentsEnabled &&
    depositRequired &&
    depositNum != null &&
    Number.isFinite(depositNum) &&
    depositNum > 0;
  const depositDeadlineLabel =
    depositDeadlineUnit === 'days'
      ? `${Math.max(1, Math.round(depositDeadlineValue))} day${Math.max(1, Math.round(depositDeadlineValue)) === 1 ? '' : 's'}`
      : `${Math.max(1, Math.round(depositDeadlineValue))} hour${Math.max(1, Math.round(depositDeadlineValue)) === 1 ? '' : 's'}`;

  const allServicesHaveProfessional =
    quoteServices.length === 0 ||
    quoteServices.every((svc) => {
      const sid = typeof svc?.id === 'string' ? svc.id : String(svc?.id ?? '');
      const proId = assignmentByServiceId[sid];
      return Boolean(proId?.trim() && professionals.some((p) => p.id === proId));
    });

  const isFormValid =
    title.trim() !== '' &&
    date.trim() !== '' &&
    (timeType === 'exact'
      ? exactTime.trim() !== ''
      : earliestTime.trim() !== '' &&
        latestTime.trim() !== '' &&
        new Date(timeToLocalISO(date.trim(), latestTime)).getTime() > new Date(timeToLocalISO(date.trim(), earliestTime)).getTime()) &&
    (!quoteHasOnSiteService || locationAddress.trim() !== '') &&
    totalAmount.trim() !== '' &&
    Number.isFinite(parseFloat(totalAmount)) &&
    parseFloat(totalAmount) >= 0 &&
    (!depositRequired || (depositAmount.trim() !== '' && Number.isFinite(parseFloat(depositAmount)) && parseFloat(depositAmount) >= 0)) &&
    allServicesHaveProfessional;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="quote-requests"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            unreadNotificationCount={0}
          />
        )}
        <div className="flex flex-col flex-1 min-w-0">
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-md hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Create Appointment</h1>
              <div className="w-10" />
            </div>
          </div>
          <div className="flex-1 p-4 md:p-8">
            <button onClick={goBack} className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            {loading ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : !quote ? (
              <div className="text-center py-12 text-gray-600">Quote not found.</div>
            ) : (
              <>
                <div className="mb-6">
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                    Create Appointment
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    {formatRequestNumber(quote.id)}
                    {quote.services?.[0]?.name && (
                      <span> • {quote.services[0].name}</span>
                    )}
                  </p>
                </div>

                <form id="create-appointment-from-quote-form" onSubmit={handleSubmit} className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8">
                  {/* Left panel — appointment details */}
                  <div className="space-y-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Appointment title</label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                        placeholder="e.g. Whole House Cleaning"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Assign Pro (per service)</label>
                      <div className="space-y-2">
                        {(quote?.services ?? []).map((svc) => {
                          const sid = typeof svc?.id === 'string' ? svc.id : String(svc?.id ?? '');
                          const serviceName = (svc as { name?: string })?.name ?? 'Service';
                          const value = assignmentByServiceId[sid] ?? '';
                          const selectedPro = value ? professionals.find((p) => p.id === value) : null;
                          const isOpen = !isSoleProfessionalLocked && openAssignDropdownServiceId === sid;
                          return (
                            <div key={sid} className="relative" data-assign-dropdown>
                              <button
                                type="button"
                                disabled={isSoleProfessionalLocked || professionalsLoading}
                                onClick={() => {
                                  if (isSoleProfessionalLocked || professionalsLoading) return;
                                  setOpenAssignDropdownServiceId((prev) => (prev === sid ? null : sid));
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-2 border border-gray-300 rounded-lg text-left focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] ${
                                  isSoleProfessionalLocked || professionalsLoading
                                    ? 'bg-gray-50 cursor-not-allowed'
                                    : 'bg-white hover:border-gray-400'
                                }`}
                                aria-disabled={isSoleProfessionalLocked || professionalsLoading}
                                title={isSoleProfessionalLocked ? 'Only one professional available — auto-assigned' : undefined}
                              >
                                <ServiceIconBadge icon={getServiceIcon(sid)} />
                                <span className="text-sm text-gray-900 font-medium min-w-0 truncate flex-1">{serviceName}</span>
                                <span className={`text-sm flex-shrink-0 ${selectedPro ? 'text-gray-500' : 'text-gray-400 italic'}`}>
                                  {selectedPro ? selectedPro.name : 'Select professional'}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${isSoleProfessionalLocked ? 'opacity-40' : ''}`} />
                              </button>
                              {isOpen && (
                                <div className="absolute top-full left-0 right-0 z-10 mt-1 py-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                                  {professionalsLoading ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>
                                  ) : professionals.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">No professionals available</div>
                                  ) : (
                                    professionals.map((p) => (
                                      <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => {
                                          setAssignmentByServiceId((prev) => ({ ...prev, [sid]: p.id }));
                                          setOpenAssignDropdownServiceId(null);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        {p.name}
                                      </button>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-start gap-6">
                      <div className="flex flex-col">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date</label>
                        <input
                          type="date"
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                          min={new Date().toISOString().slice(0, 10)}
                          className="block w-full max-w-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Time type</label>
                        <div className="flex gap-4 items-center min-h-[42px]">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="timeType"
                              checked={timeType === 'exact'}
                              onChange={() => setTimeType('exact')}
                              className="text-[#0B4778] focus:ring-[#0B4778]"
                            />
                            <span className="text-sm text-gray-700">Exact</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="timeType"
                              checked={timeType === 'window'}
                              onChange={() => setTimeType('window')}
                              className="text-[#0B4778] focus:ring-[#0B4778]"
                            />
                            <span className="text-sm text-gray-700">Window</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    {timeType === 'exact' ? (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Time</label>
                        <input
                          type="time"
                          value={exactTime}
                          onChange={(e) => setExactTime(e.target.value)}
                          className="block w-[10.5rem] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                        />
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-start gap-6">
                        <div className="flex flex-col">
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Earliest</label>
                          <input
                            type="time"
                            value={earliestTime}
                            onChange={(e) => setEarliestTime(e.target.value)}
                            className="block w-[10.5rem] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Latest</label>
                          <input
                            type="time"
                            value={latestTime}
                            onChange={(e) => setLatestTime(e.target.value)}
                            className="block w-[10.5rem] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                          />
                        </div>
                      </div>
                    )}

                    <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={recurringEnabled}
                          onChange={(e) => setRecurringEnabled(e.target.checked)}
                          className="rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778]"
                        />
                        <span className="text-sm font-medium text-gray-900">Recurring series</span>
                      </label>
                      <p className="text-xs text-gray-500">
                        Creates multiple appointments on a fixed weekly interval. With a time window, the series is scheduled from the start of the window. Assigning a pro for each service uses stricter availability matching.
                      </p>
                      {recurringEnabled && (
                        <div className="flex flex-wrap gap-6">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Every (weeks)</label>
                            <select
                              value={recurringIntervalWeeks}
                              onChange={(e) => setRecurringIntervalWeeks(Number(e.target.value) || 1)}
                              className="block w-[8rem] px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                            >
                              {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Number of visits</label>
                            <select
                              value={recurringOccurrenceCount}
                              onChange={(e) => setRecurringOccurrenceCount(Number(e.target.value) || 1)}
                              className="block w-[8rem] px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                            >
                              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                    {quoteHasOnSiteService && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Location / Address</label>
                        <textarea
                          value={locationAddress}
                          onChange={(e) => setLocationAddress(e.target.value)}
                          rows={2}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                          placeholder="Enter service address..."
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message to customer (optional)</label>
                      <textarea
                        value={messageToCustomer}
                        onChange={(e) => setMessageToCustomer(e.target.value)}
                        rows={2}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                        placeholder="Short note to the customer..."
                      />
                    </div>
                    {showDepositPaymentInstructions && (
                      <div className="flex items-start gap-3 rounded-lg border border-[#E6D0A3] bg-[#FFF9E7] p-4 text-[#785413]">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#F5E6C8] text-[#785413]">
                          <AlertTriangle className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 text-sm leading-relaxed">
                          <p className="font-semibold">Deposit payment instructions needed</p>
                          <p className="mt-1">
                            Online payments are not set up for this business. Since a deposit is required, include how the customer should send it, such as Venmo, Zelle, check, or another accepted method.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right panel — preview & payment */}
                  <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">Customer-facing preview</h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="text-gray-500">Time (window):</span>{' '}
                          {timeType === 'window'
                            ? `${earliestTime} – ${latestTime}`
                            : timeType === 'exact'
                              ? exactTime
                              : '—'}
                        </p>
                        {recurringEnabled && (
                          <p>
                            <span className="text-gray-500">Recurring:</span>{' '}
                            {recurringOccurrenceCount} visit{recurringOccurrenceCount === 1 ? '' : 's'}, every{' '}
                            {recurringIntervalWeeks} week{recurringIntervalWeeks === 1 ? '' : 's'}
                          </p>
                        )}
                        <div>
                          <span className="text-gray-500">Assigned:</span>
                          <ul className="mt-1 list-disc list-inside text-sm">
                            {quoteServices.length === 0 ? (
                              <li>—</li>
                            ) : (
                              quoteServices.map((svc) => {
                                const sid = typeof svc?.id === 'string' ? svc.id : String(svc?.id ?? '');
                                const proId = assignmentByServiceId[sid];
                                const pro = proId ? professionals.find((p) => p.id === proId) : null;
                                const name = (svc as { name?: string })?.name ?? 'Service';
                                return (
                                  <li key={sid} className="flex items-center gap-1.5">
                                    <ServiceIconBadge icon={getServiceIcon(sid)} />
                                    <span>{name}: {pro?.name ?? '—'}</span>
                                  </li>
                                );
                              })
                            )}
                          </ul>
                        </div>
                        <p>
                          <span className="text-gray-500">Deposit:</span>{' '}
                          {depositRequired && depositAmount.trim() !== '' ? `$${Number(depositAmount).toFixed(2)}` : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">Payment</h3>
                        {!onlinePaymentsEnabled && (
                          <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-[#E6D0A3] bg-[#FFF9E7] px-2.5 py-1 text-xs font-semibold text-[#785413]">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                            Online payment not set up
                          </span>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Total</label>
                          <div className="flex items-center rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm focus-within:ring-2 focus-within:ring-[#0B4778] focus-within:border-[#0B4778]">
                            <span className="px-3 py-2 bg-gray-50 text-gray-500 border-r border-gray-200">$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={totalAmount}
                              onChange={(e) => setTotalAmount(e.target.value)}
                              className="flex-1 min-w-0 px-3 py-2 border-0 bg-transparent focus:ring-0 focus:outline-none"
                            />
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={depositRequired} onChange={(e) => setDepositRequired(e.target.checked)} className="rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778]" />
                          <span className="text-sm text-gray-700">Deposit required</span>
                        </label>
                        {depositRequired && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Deposit amount</label>
                            <div className="flex items-center rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm focus-within:ring-2 focus-within:ring-[#0B4778] focus-within:border-[#0B4778]">
                              <span className="px-3 py-2 bg-gray-50 text-gray-500 border-r border-gray-200">$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={depositAmount}
                                onChange={(e) => setDepositAmount(e.target.value)}
                                className="flex-1 min-w-0 px-3 py-2 border-0 bg-transparent focus:ring-0 focus:outline-none"
                              />
                            </div>
                          </div>
                        )}
                        {depositRequired && (
                          <div className="rounded-lg border border-rose-200 bg-rose-50/80 p-4 text-rose-950">
                            <div className="flex items-center gap-2">
                              <Timer className="h-4 w-4 shrink-0 text-rose-700" />
                              <p className="text-sm font-semibold text-rose-950">Deposit deadline</p>
                            </div>
                            <p className="mt-2 text-sm text-rose-900">
                              Cancel appointment if deposit is not received within
                            </p>
                            <div className="mt-2 grid grid-cols-2 gap-2 w-full">
                              <input
                                type="number"
                                min={1}
                                value={depositDeadlineValue}
                                onChange={(e) => {
                                  const next = parseInt(e.target.value, 10);
                                  setDepositDeadlineValue(Number.isFinite(next) && next > 0 ? next : 1);
                                }}
                                className="w-full min-w-0 px-3 py-2 border border-rose-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                              />
                              <select
                                value={depositDeadlineUnit}
                                onChange={(e) => setDepositDeadlineUnit(e.target.value === 'days' ? 'days' : 'hours')}
                                className="w-full min-w-0 px-3 py-2 border border-rose-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                              >
                                <option value="hours">Hours</option>
                                <option value="days">Days</option>
                              </select>
                            </div>
                            <p className="mt-3 text-sm text-rose-900">
                              Use this setting when the deposit is handled outside AACore Booking and must be confirmed manually.
                            </p>
                            {depositNum != null && Number.isFinite(depositNum) && depositNum > 0 && (
                              <p className="mt-3 text-sm font-semibold text-[#5C4A1F]">
                                Appointment may be canceled if the ${depositNum.toFixed(2)} deposit is not received within {depositDeadlineLabel}.
                              </p>
                            )}
                          </div>
                        )}
                        {remaining != null && remaining >= 0 && (
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <span className="inline-block w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-xs flex items-center justify-center">i</span>
                            ${remaining.toFixed(2)} after deposit
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Internal notes (optional)</label>
                      <textarea
                        value={internalNotes}
                        onChange={(e) => setInternalNotes(e.target.value)}
                        rows={2}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                        placeholder="Staff-only notes..."
                      />
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} className="rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778]" />
                        <span className="text-sm text-gray-700">Notify customer</span>
                      </label>
                    </div>
                  </div>
                </form>

                {/* Footer buttons — full width below grid */}
                <div className="flex justify-end gap-2 mt-8 border-t border-gray-200 pt-6">
                  <button type="button" onClick={goBack} className="aacore-btn2-secondary flex items-center gap-2" disabled={submitting}>
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="create-appointment-from-quote-form"
                    disabled={submitting || !isFormValid}
                    className="aacore-btn2-primary flex items-center gap-2"
                    style={{ backgroundColor: '#0B4778' }}
                  >
                    <CalendarPlus className="w-4 h-4" />
                    {submitting ? 'Creating…' : 'Create Appointment'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAppointmentFromQuotePage;
